import { CheckCircle2, ExternalLink, LayoutGrid, Mail, MessageCircle, PencilLine, Presentation as PresentationIcon, Search } from "lucide-react";
import { StepShell, GhostButton } from "./ui";
import { track } from "../../lib/journey";

const SUMMARY = [
  ["purpose", "Purpose"], ["city", "City"], ["area", "Area"], ["property_type", "Type"], ["configuration", "Configuration"],
  ["budget", "Budget"], ["possession", "Possession"], ["priorities", "Priorities"], ["visit_timing", "India visit"],
];

const Presentation = ({ url }) => (
  <div className="mt-10" data-testid="sample-presentation">
    <span className="inline-block rounded-full bg-[var(--ivory)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--saffron-deep)]">
      Example presentation
    </span>
    <h3 className="mt-3 font-display text-2xl text-[#1f2328]">Here's how MatchMaking helps you explore a project before visiting</h3>
    {url ? (
      <a href={url} target="_blank" rel="noopener noreferrer" onClick={() => track("example_view")} className="btn-saffron mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full text-base font-semibold" data-testid="view-matchmaking-example-button">
        <PresentationIcon className="h-5 w-5" /> View MatchMaking Example <ExternalLink className="h-4 w-4" />
      </a>
    ) : (
      <button disabled className="btn-saffron mt-5 flex h-14 w-full items-center justify-center gap-2 rounded-full text-base font-semibold" data-testid="view-matchmaking-example-button-disabled">
        <PresentationIcon className="h-5 w-5" /> MatchMaking Example coming soon
      </button>
    )}
    <p className="mt-2 text-xs text-neutral-500">Opens in a new tab · Powered by ConectR MatchMaking Technology</p>
  </div>
);

const Cart = ({ cart }) => (
  <div className="mt-8 rounded-3xl border border-[#138808]/30 bg-[#138808]/[0.04] p-5" data-testid="thanks-cart">
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0f6d06]">Your MatchMaking cart · {cart.length} project{cart.length === 1 ? "" : "s"}</p>
    <ul className="mt-3 divide-y divide-neutral-200/70">
      {cart.map((p) => (
        <li key={p.ref} className="flex items-center justify-between gap-3 py-2.5 text-sm" data-testid={`thanks-cart-item-${p.ref}`}>
          <span className="font-medium text-[#1f2328]">{[p.bhk, p.property_type === "Flat" ? "Apartment" : p.property_type].filter(Boolean).join(" ")} in {p.locality}</span>
          <span className="shrink-0 text-xs text-neutral-500">{p.price} · {p.match_pct}%</span>
        </li>
      ))}
    </ul>
  </div>
);

const SearchMore = ({ canRelook, onRelook, onSearchMore }) => (
  <div className="mt-8 rounded-3xl border border-[#FF9933]/40 bg-[var(--ivory)] p-5" data-testid="thanks-search-more">
    <h3 className="font-display text-xl text-[#1f2328]">Want to explore more options?</h3>
    <p className="mt-1.5 text-sm text-neutral-600">
      {canRelook ? "Look through your matches again and add more projects, or" : "You can"} try a different city, budget or property type. Your current request stays with our team, and your details are already filled in.
    </p>
    <div className="mt-4 grid gap-2.5">
      <button onClick={onSearchMore} className="btn-saffron flex h-12 items-center justify-center gap-2 rounded-full text-sm font-semibold" data-testid="thanks-search-more-button">
        <Search className="h-4 w-4" /> Search more properties
      </button>
      {canRelook && (
        <GhostButton onClick={onRelook} className="h-12" data-testid="thanks-relook-matches-button">
          <LayoutGrid className="h-4 w-4" /> See my matches again
        </GhostButton>
      )}
    </div>
  </div>
);

export const ThankYou = ({ name, email, cart = [], answers, conectrUrl, onEdit, justUpdated, canRelook, onRelook, onSearchMore }) => (
  <StepShell stepKey="thanks" testId="screen-thanks">
    <CheckCircle2 className="h-10 w-10 text-[#138808]" strokeWidth={1.6} />
    <h1 className="mt-4 font-display text-[2rem] font-medium leading-tight text-[#1f2328] sm:text-5xl" data-testid="thanks-heading">
      Thank you, {name}.
    </h1>
    <p className="mt-4 text-base text-neutral-600">
      {cart.length
        ? `Our team is preparing MatchMaking presentations for the ${cart.length} project${cart.length === 1 ? "" : "s"} you chose. You'll receive them by email and on WhatsApp chat within 24 hours.`
        : "Our team is shortlisting 5–7 projects that match your needs. You'll receive their MatchMaking presentations by email and on WhatsApp chat within 24 hours."}
    </p>
    <p className="mt-3 flex items-center gap-2 text-sm font-medium text-[#0f6d06]" data-testid="thanks-whatsapp-only-note">
      <MessageCircle className="h-4 w-4" /> WhatsApp chat only — we will not call you.
    </p>
    {email && (
      <p className="mt-3 flex items-center gap-2 text-sm text-neutral-500" data-testid="thanks-email-copy-note">
        <Mail className="h-4 w-4 text-[#138808]" /> You'll find a copy of your requirements in your inbox at {email}.
      </p>
    )}
    {justUpdated && (
      <p className="mt-4 rounded-xl bg-[#138808]/10 px-4 py-3 text-sm text-[#0f6d06]" data-testid="thanks-updated-notice">
        Your requirements have been updated. Our team will use the latest details.
      </p>
    )}
    {cart.length > 0 && <Cart cart={cart} />}
    <div className="mt-8 rounded-3xl border border-neutral-200 p-5" data-testid="thanks-summary">
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-neutral-500">Your requirements</p>
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
        {SUMMARY.filter(([k]) => (Array.isArray(answers[k]) ? answers[k].length : answers[k])).map(([k, label]) => (
          <div key={k} className="contents">
            <dt className="text-neutral-500">{label}</dt>
            <dd className="font-medium text-[#1f2328]">{Array.isArray(answers[k]) ? answers[k].join(", ") : answers[k]}</dd>
          </div>
        ))}
      </dl>
      <GhostButton onClick={onEdit} className="mt-5 h-12" data-testid="thanks-edit-requirements-button">
        <PencilLine className="h-4 w-4" /> Edit my requirements
      </GhostButton>
    </div>
    <SearchMore canRelook={canRelook} onRelook={onRelook} onSearchMore={onSearchMore} />
    <Presentation url={conectrUrl} />
  </StepShell>
);
